import { Box, styled, Typography } from '@mui/material';

import {
  CarIcon,
  CheckIcon,
  ClockIcon,
  GearStatsIcon,
  Loader,
  NoCrewIcon,
  PlaneIcon,
  PlaneOnFlyIcon,
  TwoArrowsIcon,
  useManifest,
} from '#shared';

import { useStatisticsData } from '../../data-context';

import { StatisticsItem } from './StatisticsItem';

const PREFIX = 'TMDX-StatisticsPage';
export const statisticsPageClasses = {
  root: `${PREFIX}-root`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  subtitle: `${PREFIX}-subtitle`,
  content: `${PREFIX}-content`,
  card: `${PREFIX}-card`,
  cardHeader: `${PREFIX}-cardHeader`,
  count: `${PREFIX}-count`,
  helperText: `${PREFIX}-helperText`,
  cardRow: `${PREFIX}-cardRow`,
  cardRowHeader: `${PREFIX}-cardRowTitleBox`,
  cardRowTitle: `${PREFIX}-cardRowTitle`,
  cardRowSubTitle: `${PREFIX}-cardRowSubTitle`,
  cardRowContent: `${PREFIX}-cardRowContent`,
  cardRowContentInner: `${PREFIX}-cardRowContentInner`,
} as const;

const Root = styled(Box, {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})(({ theme }) => {
  const { utils, palette, spacing } = theme;

  const gap = utils.clamp(4, 8);
  const cardGap = utils.clamp(8, 14);
  const cardPadY = utils.clamp(8, 12);
  const cardPadX = utils.clamp(8, 12);

  return {
    [`&.${statisticsPageClasses.root}`]: {
      display: 'grid',
      gap,
      fontFamily: `'JetBrains Mono', monospace`,
      fontFeatureSettings: `'zero' 1`,
      width: '100%',
      height: '100%',
      minWidth: 0,
      minHeight: 0,
      gridTemplateRows: '1fr',
    },
    [`.${statisticsPageClasses.title}`]: {
      lineHeight: spacing(4),
      fontSize: utils.clamp(16, 16),
      color: '#DDDDDD',
      textAlign: 'center',
    },
    [`.${statisticsPageClasses.subtitle}`]: {
      fontSize: utils.clamp(12, 14),
      lineHeight: spacing(2),
      color: '#7F8083',
      textAlign: 'center',
    },
    [`.${statisticsPageClasses.content}`]: {
      display: 'flex',
      gap,
      flexWrap: 'nowrap',
      alignItems: 'stretch',
    },
    [`.${statisticsPageClasses.card}`]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: cardGap,
      flexGrow: 1,
      minWidth: 0,
      padding: `${cardPadY} ${cardPadX}`,
      height: '100%',
      borderWidth: 1,
      borderRadius: 12,
    },
    [`.${statisticsPageClasses.cardHeader}`]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1,
      color: '#B6B6B6',
    },
    [`.${statisticsPageClasses.count}`]: {
      fontSize: utils.clamp(14, 16),
      lineHeight: spacing(2),
    },
    [`.${statisticsPageClasses.helperText}`]: {
      fontSize: utils.clamp(12, 14),
      lineHeight: spacing(1.5),
      textAlign: 'center',
    },
    [`.${statisticsPageClasses.cardRow}`]: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'stretch',
      width: '100%',
    },
    [`.${statisticsPageClasses.cardRowHeader}`]: {
      flex: '1 0 0',
      minWidth: 0,
      maxWidth: 'min(33%, 200px)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#B6B6B6',
      userSelect: 'none',
      height: '100%',
      padding: spacing(1),
      border: `1px solid ${palette.mapBlue[700]}`,
      borderTopLeftRadius: 10,
      borderBottomRightRadius: 10,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
    [`.${statisticsPageClasses.cardRowTitle}`]: {
      letterSpacing: -0.5,
      fontFamily: 'inherit',
      fontSize: utils.clamp(16, 22, 'sm-4k'),
      fontWeight: 600,
    },
    [`.${statisticsPageClasses.cardRowSubTitle}`]: {
      fontFamily: 'inherit',
      fontSize: utils.clamp(12, 16, 'md-4k'),
      textAlign: 'center',
      letterSpacing: -0.5,
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    [`.${statisticsPageClasses.cardRowContent}`]: {
      flex: '1 1 0',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      padding: 0,
      minHeight: 0,
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'visible',
    },
    [`.${statisticsPageClasses.cardRowContentInner}`]: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'nowrap',
      overflowX: 'auto',
      width: '100%',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      paddingLeft: spacing(1.5),
      gap: spacing(0, 0.5),
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
  };
});

export const WidgetStatisticsContent = () => {
  const { statistics, statisticsData } = useStatisticsData();
  useManifest();

  return (
    (statistics && statisticsData && (
      <Root className={statisticsPageClasses.root}>
        <div className={statisticsPageClasses.content}>
          <div className={statisticsPageClasses.card}>
            <Box className={statisticsPageClasses.cardRow}>
              <Box className={statisticsPageClasses.cardRowHeader}>
                <Typography className={statisticsPageClasses.cardRowTitle}>
                  {statistics.casesStatistics.allCases}
                </Typography>
                <Typography
                  className={statisticsPageClasses.cardRowSubTitle}
                  title={'Total Cases'}
                >
                  Total Cases
                </Typography>
              </Box>
              <div className={statisticsPageClasses.cardRowContent}>
                <Box className={statisticsPageClasses.cardRowContentInner}>
                  <StatisticsItem
                    icon={<PlaneOnFlyIcon />}
                    percents={`${statisticsData.airGround}`}
                    bg={statisticsData.airGroundBg}
                    extraIcon={<CarIcon />}
                    extraPercents={`${statisticsData.ground}`}
                    label="Air/Ground"
                    mainColor="#0e5795"
                    insideWidget={true}
                  />
                  <StatisticsItem
                    icon={<PlaneIcon />}
                    bg={statisticsData.tmdxShareBg}
                    percents={`${statisticsData.tmdxShare}`}
                    label="TMDX Avia"
                    insideWidget={true}
                  />
                </Box>
              </div>
            </Box>

            <Box className={statisticsPageClasses.cardRow}>
              <Box className={statisticsPageClasses.cardRowHeader}>
                <Typography className={statisticsPageClasses.cardRowTitle}>
                  {statisticsData.crewTotalHours} h
                </Typography>
                <Typography
                  className={statisticsPageClasses.cardRowSubTitle}
                  title={'TMDX Avia Available'}
                >
                  TMDX Avia Available
                </Typography>
              </Box>
              <div className={statisticsPageClasses.cardRowContent}>
                <Box className={statisticsPageClasses.cardRowContentInner}>
                  <StatisticsItem
                    icon={<ClockIcon />}
                    bg={statisticsData.tmdxWaitingForCaseBg}
                    percents={`${statisticsData.tmdxWaitingForCase}`}
                    hours={`${statisticsData.crewWaitingForCaseHours}`}
                    label="Waiting"
                    insideWidget={true}
                  />
                  <StatisticsItem
                    icon={<CheckIcon />}
                    bg={statisticsData.tmdxOnCaseBg}
                    percents={`${statisticsData.tmdxOnCase}`}
                    hours={`${statisticsData.crewOnCaseHours}`}
                    label="On Case"
                    insideWidget={true}
                  />
                  <StatisticsItem
                    icon={<TwoArrowsIcon />}
                    bg={statisticsData.tmdxNonCaseBg}
                    percents={`${statisticsData.tmdxNonCase}`}
                    hours={`${statisticsData.crewOnNonCaseHours}`}
                    label="Non-Case"
                    insideWidget={true}
                  />
                </Box>
              </div>
            </Box>

            <Box className={statisticsPageClasses.cardRow}>
              <Box className={statisticsPageClasses.cardRowHeader}>
                <Typography className={statisticsPageClasses.cardRowTitle}>
                  {statisticsData.maintenancesHours +
                    statisticsData.noCrewHours}{' '}
                  h
                </Typography>
                <Typography
                  className={statisticsPageClasses.cardRowSubTitle}
                  title={'TMDX Avia Unavailable'}
                >
                  TMDX Avia Unavailable
                </Typography>
              </Box>
              <div className={statisticsPageClasses.cardRowContent}>
                <Box className={statisticsPageClasses.cardRowContentInner}>
                  <StatisticsItem
                    bg={statisticsData.tmdxNoCrewBg}
                    icon={<NoCrewIcon />}
                    percents={`${statisticsData.tmdxNoCrew}`}
                    hours={`${statisticsData.noCrewHours}`}
                    label="No Crew"
                    insideWidget={true}
                  />
                  <StatisticsItem
                    icon={<GearStatsIcon />}
                    bg={statisticsData.tmdxMaintenanceBg}
                    percents={`${statisticsData.tmdxMaintenance}`}
                    hours={`${statisticsData.maintenancesHours}`}
                    label="Maintenance"
                    insideWidget={true}
                  />
                </Box>
              </div>
            </Box>
          </div>
        </div>
      </Root>
    )) || <Loader />
  );
};
